import React, { createContext, useContext, useState, useEffect } from "react";
import { getUserToken, logoutUser } from '../api/authenticationApi';
import { get } from '../api/axiosInstance';
import { buildUserFromClaims, decodeJwtPayload } from '../utils/jwtUtils';

const SESSION_POLL_MS = 10000;

// Create the Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSessionTimedOut, setIsSessionTimedOut] = useState(false);
  const [isKickedOut, setIsKickedOut] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    const storedUser = JSON.parse(localStorage.getItem("user") || "null");
    const tokenClaims = token
      ? buildUserFromClaims(decodeJwtPayload(token), storedUser?.username)
      : null;
    const resolvedUser = tokenClaims?.username ? tokenClaims : storedUser;
    if (resolvedUser) {
      setUser(resolvedUser);
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  // Session validity polling � kicks Device A out when Device B force-logs in
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkSession = async () => {
      try {
        await get('/api/Auth/ValidateSession');
      } catch {
      }
    };

    const intervalId = setInterval(checkSession, SESSION_POLL_MS);

    // Instant check when the tab comes back into focus
    const handleVisibility = () => { if (!document.hidden) checkSession(); };
    // Instant check when the device reconnects
    const handleOnline = () => checkSession();

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
    };
  }, [isAuthenticated]);

  // Login function
  // Returns: { success: true }
  //        | { success: false, sessionConflict: true }  � account active on another device
  //        | { success: false }                         � wrong credentials / server error
  const login = async (username, password, forceLogin = false) => {
    const result = await getUserToken(username, password, forceLogin);

    if (result.sessionConflict) {
      return { success: false, sessionConflict: true };
    }

    if (result.isSuccess) {
      const token = result.data?.accessToken ?? result.data?.AccessToken;
      if (!token) {
        return { success: false };
      }
      const claims = decodeJwtPayload(token);
      const userData = buildUserFromClaims(claims, username) ?? { username };
      setUser(userData);
      setIsAuthenticated(true);
      setIsSessionTimedOut(false);
      setIsKickedOut(false);
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("accessToken", token);
      return { success: true };
    }

    return { success: false };
  };

  // Logout function.
  // Pass true when the session was terminated by another device login.
  const logout = async (kicked = false) => {
    await logoutUser();
    setUser(null);
    setIsAuthenticated(false);
    setIsSessionTimedOut(false);
    setIsKickedOut(kicked);
    localStorage.clear();
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        isAuthenticated, 
        setIsAuthenticated,
        isSessionTimedOut,
        setIsSessionTimedOut,
        isKickedOut,
        setIsKickedOut,
        login, 
        logout, 
        loading 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom Hook to Use Auth Context
export const useAuth = () => useContext(AuthContext);
